import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Heart, TrendingUp } from 'lucide-react';
import { urlFor } from '../../lib/sanity';
import { getPetUrlPath } from '../../lib/petUtils';
import type { Pet } from '../../types/sanity';

interface PetDetailsProps {
  pet: Pet;
}

export function PetDetails({ pet }: PetDetailsProps) {
  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return 'Nieznany wiek';
    const today = new Date();
    const birth = new Date(birthDate);
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    if (ageInMonths < 12) return `${ageInMonths} mies.`;
    const years = Math.floor(ageInMonths / 12);
    return `${years} ${years === 1 ? 'rok' : years < 5 ? 'lata' : 'lat'}`;
  };

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'available': 'Do adopcji',
      'reserved': 'Zarezerwowany',
      'adopted': 'Adoptowany',
      'not_adoptable': 'Nie do adopcji',
      'deceased': 'Za tęczowym mostem'
    };
    return statusMap[status] || status;
  };

  const getSizeLabel = (size: string) => {
    const sizeMap: { [key: string]: string } = {
      'mini': 'Miniaturka',
      'small': 'Mały',
      'medium': 'Średni',
      'large': 'Duży'
    };
    return sizeMap[size] || size;
  };

  const getGenderLabel = (gender: string) => {
    const genderMap: { [key: string]: string } = {
      'male': 'Samiec',
      'female': 'Samica',
      'unknown': 'Nieznana'
    };
    return genderMap[gender] || gender;
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="heading-1 mb-4">
          {pet.name} {pet.status !== 'available' && (
            <span className="text-accessible-red-600">
              ({getStatusLabel(pet.status)})
            </span>
          )}
        </h1>
        <div className="flex flex-wrap gap-4 mb-6">
          <span className="px-4 py-2 bg-accessible-gray-100 dark:bg-accessible-gray-700 high-contrast:bg-white text-accessible-gray-700 dark:text-white high-contrast:text-black rounded-full tag-text">
            {pet.mainCategory === 'dog' ? 'Pies' : 'Kot'}
          </span>
          <span className="px-4 py-2 bg-accessible-gray-100 dark:bg-accessible-gray-700 high-contrast:bg-white text-accessible-gray-700 dark:text-white high-contrast:text-black rounded-full tag-text">
            {getGenderLabel(pet.gender)}
          </span>
          <span className="px-4 py-2 bg-accessible-gray-100 dark:bg-accessible-gray-700 high-contrast:bg-white text-accessible-gray-700 dark:text-white high-contrast:text-black rounded-full tag-text">
            {getSizeLabel(pet.size)}
          </span>
          {pet.subCategory && (
            <span className="px-4 py-2 bg-accessible-gray-100 dark:bg-accessible-gray-700 high-contrast:bg-white text-accessible-gray-700 dark:text-white high-contrast:text-black rounded-full tag-text">
              {pet.subCategory}
            </span>
          )}
          {pet.birthDate && (
            <span className="px-4 py-2 bg-accessible-gray-100 dark:bg-accessible-gray-700 high-contrast:bg-white text-accessible-gray-700 dark:text-white high-contrast:text-black rounded-full tag-text">
              Wiek: {calculateAge(pet.birthDate)}
            </span>
          )}
        </div>
        {pet.status === 'deceased' && (
          <div className="mb-6 p-4 bg-accessible-gray-100 dark:bg-accessible-gray-700 high-contrast:bg-white high-contrast:text-black rounded-lg">
            <h2 className="heading-5 mb-1">Za tęczowym mostem</h2>
            {pet.deceasedDate && (
              <p className="body-medium no-margin">Data odejścia: {new Date(pet.deceasedDate).toLocaleDateString('pl-PL')}</p>
            )}
            {pet.deceasedDescription && (
              <p className="body-medium mt-2 whitespace-pre-line">{pet.deceasedDescription}</p>
            )}
          </div>
        )}
        {pet.shelterMates && pet.shelterMates.length > 0 && (
          <div className="mb-6 p-4 bg-accessible-gray-100 dark:bg-accessible-gray-700 high-contrast:bg-white high-contrast:text-black rounded-lg">
            <h2 className="heading-5 mb-3">
              Mieszka razem z{pet.shelterMates.length > 1 ? ':' : ''}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pet.shelterMates.map((mate) => (
                <div key={mate._id} className="flex items-center gap-4">
                  <img
                    src={urlFor(mate.image)}
                    alt={mate.name}
                    className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                  />
                  <div>
                    <p className="label-large no-margin">
                      {mate.name}
                    </p>
                    <Link
                      to={getPetUrlPath(mate)}
                      className="link-primary"
                    >
                      Zobacz profil
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {pet.tags && pet.tags.length > 0 && (
        <div className="mb-6">
          <h2 className="heading-5 mb-3">Cechy i uwagi</h2>
          <div className="flex flex-wrap gap-2">
            {pet.tags.map((tag) => (
              <span key={tag._ref} className="px-3 py-1 bg-accessible-red-600/10 dark:bg-white/10 text-accessible-red-600 dark:text-white rounded-full body-small">
                {tag.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {pet.isUrgentFundraising && pet.fundraisingGoal && (
        <div className="mb-8 p-6 bg-accessible-red-600/10 border-2 border-accessible-red-600/20 rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-8 h-8 text-accessible-red-600 animate-pulse" />
            <div>
              <h2 className="heading-4 text-accessible-red-600 mb-1">
                PILNA ZBIÓRKA
              </h2>
              <p className="body-medium text-accessible-red-600 no-margin">
                {pet.fundraisingDescription}
              </p>
            </div>
          </div>
          
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="label-large">
                Zebrano: {(pet.fundraisingCurrent || 0).toLocaleString('pl-PL')} zł
              </span>
              <span className="label-large">
                Cel: {pet.fundraisingGoal.toLocaleString('pl-PL')} zł
              </span>
            </div>
            <div className="h-4 bg-accessible-gray-200 dark:bg-accessible-gray-600 rounded-full overflow-hidden">
              <div 
                className="h-full bg-accessible-red-600 rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-2"
                style={{ width: `${Math.round((pet.fundraisingCurrent || 0) / pet.fundraisingGoal * 100)}%` }}
              >
                <TrendingUp className="w-3 h-3 text-white" />
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="body-small text-accessible-gray-600 dark:text-accessible-gray-400">
                {Math.round((pet.fundraisingCurrent || 0) / pet.fundraisingGoal * 100)}% zebrane
              </span>
              <span className="body-small text-accessible-red-600 font-medium">
                Pozostało: {(pet.fundraisingGoal - (pet.fundraisingCurrent || 0)).toLocaleString('pl-PL')} zł
              </span>
            </div>
          </div>
          
          <a
            href="/wplata-jednorazowa"
            className="w-full bg-accessible-red-600 text-white py-4 px-6 rounded-lg hover:bg-accessible-red-700 transition-colors flex items-center justify-center gap-2 font-bold text-lg shadow-lg"
          >
            <Heart className="w-6 h-6" />
            <span>POMÓŻ {pet.name.toUpperCase()}</span>
          </a>
        </div>
      )}

      <div className="mb-8">
        <p className="body-large whitespace-pre-line">
          {pet.description}
        </p>
      </div>
    </div>
  );
}